import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
} from "@xyflow/react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface EdgeLabel {
  text: string;
  position: "start" | "middle" | "end";
  color?: string;
  configurable?: boolean;
}

export interface EdgeState {
  active: boolean;
  lastTriggered?: Date;
  executionCount: number;
  condition?: string;
  metadata?: Record<string, unknown>;
}

export interface DataFlowInfo {
  dataType: string;
  schema?: Record<string, unknown>;
  sampleData?: unknown;
}

export interface AdvancedEdgeData {
  labels?: EdgeLabel[];
  state?: EdgeState;
  processor?: string;
  multiTarget?: boolean;
  targets?: string[];
  dataFlow?: DataFlowInfo;
}

export const DataEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
}: EdgeProps & { data?: AdvancedEdgeData }) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const state = data?.state;
  const dataFlow = data?.dataFlow;

  return (
    <>
      <BaseEdge 
        path={edgePath} 
        markerEnd={markerEnd} 
        style={{
          ...style,
          stroke: state?.active ? "#10b981" : (selected ? "#3b82f6" : "#64748b"),
          strokeWidth: state?.active ? 3 : (selected ? 2 : 1),
          strokeDasharray: dataFlow ? "5,5" : "none",
        }} 
      />
      <EdgeLabelRenderer>
        {dataFlow && (
          <div
            className={cn(
              "absolute transform -translate-x-1/2 -translate-y-1/2",
              "pointer-events-auto bg-background border rounded px-2 py-1",
              "text-xs shadow-sm"
            )}
            style={{ 
              left: labelX, 
              top: labelY,
              transform: "translate(-50%, -50%)" 
            }}
          >
            <div className="font-medium text-foreground">
              {dataFlow.dataType}
            </div>
            {state && (
              <div className="text-muted-foreground">
                {state.executionCount}x transferred
              </div>
            )}
          </div>
        )}
        {data?.labels?.map((label, index) => (
          <div
            key={index}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
            style={{ 
              left: label.position === "start" ? sourceX + (labelX - sourceX) * 0.2 : 
                    label.position === "end" ? targetX + (labelX - targetX) * 0.2 : labelX,
              top: label.position === "start" ? sourceY + (labelY - sourceY) * 0.2 : 
                   label.position === "end" ? targetY + (labelY - targetY) * 0.2 : labelY 
            }}
          >
            <Badge
              variant={state?.active ? "default" : "secondary"}
              className={cn(
                "cursor-pointer",
                label.configurable && "hover:bg-primary/20"
              )}
              style={{ backgroundColor: label.color }}
            >
              {label.text}
            </Badge>
          </div>
        ))}
      </EdgeLabelRenderer>
    </>
  );
};
